import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { dateLabel } from '@adisys/shared';
import { api } from '../../lib/api';
import { qtyLabel } from '../../lib/crm';
import { Button, ErrorState, Skeleton } from '../../components/ui';
import { ProposalDocument } from './ProposalDocument';

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen',
  'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const below100 = (n: number) => (n < 20 ? ONES[n]! : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ''}`);
const below1000 = (n: number) => `${n >= 100 ? `${ONES[Math.floor(n / 100)]} Hundred${n % 100 ? ' ' : ''}` : ''}${n % 100 ? below100(n % 100) : ''}`;

/** Indian numbering: crore, lakh, thousand. */
function wholeInWords(n: number): string {
  if (n === 0) return 'Zero';
  const parts: string[] = [];
  const crore = Math.floor(n / 10_000_000); n %= 10_000_000;
  const lakh = Math.floor(n / 100_000); n %= 100_000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  if (crore) parts.push(`${wholeInWords(crore)} Crore`);
  if (lakh) parts.push(`${below100(lakh)} Lakh`);
  if (thousand) parts.push(`${below100(thousand)} Thousand`);
  if (n) parts.push(below1000(n));
  return parts.join(' ');
}

export function amountInWords(amount: number): string {
  const total = Math.round(Number(amount) * 100);
  const rupees = Math.floor(total / 100), paise = total % 100;
  return `Indian Rupees ${wholeInWords(rupees)}${paise ? ` and ${below100(paise)} Paise` : ''} Only`;
}

const fmt = (n: number | string) => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n ?? 0));

export function QuotationDocumentPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: q, isLoading, error, refetch } = useQuery({ queryKey: ['crm', 'quotation-doc', id], queryFn: () => api.get(`/crm/quotations/${id}`) });

  if (isLoading) return <div className="p-8"><Skeleton className="h-96 w-full" /></div>;
  if (error || !q) return <ErrorState error={error} onRetry={() => refetch()} />;

  const title = q.kind === 'proforma' ? 'Proforma Invoice' : 'Quotation';
  const c = q.customer ?? {};
  const co = q.company ?? {};
  // Quotations print as the full proposal; the compact tax-style sheet stays for proforma invoices and on request.
  const proposal = q.kind === 'quotation' && !!q.proposal && params.get('view') !== 'compact';
  const hasDiscount = (q.lines as any[]).some((l) => Number(l.discount) > 0);

  return (
    <div className="min-h-dvh bg-ink-100 print:bg-white">
      <style>{`
        @page { size: A4; margin: ${proposal ? '0' : '14mm'}; }
        @media print { .proposal-sheet { box-shadow: none !important; margin: 0 !important; max-width: none !important; } }
        @media print { .doc-sheet { box-shadow: none !important; margin: 0 !important; padding: 0 !important; max-width: none !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      `}</style>

      <div className="no-print sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-line bg-card px-4 py-2.5">
        <Button size="sm" icon={<ArrowLeft className="h-4 w-4" />} onClick={() => (window.history.length > 1 ? navigate(-1) : window.close())}>Back</Button>
        <div className="flex items-center gap-2">
          {q.kind === 'quotation' && q.proposal && (
            <Button size="sm" variant="ghost" onClick={() => setParams(proposal ? { view: 'compact' } : {}, { replace: true })}>
              {proposal ? 'Compact tax view' : 'Full proposal view'}</Button>)}
          <span className="hidden text-xs text-ink-500 md:inline">In the print dialog set Margins to None and untick “Headers and footers”.</span>
          <Button size="sm" variant="primary" icon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>Print / Save as PDF</Button>
        </div>
      </div>

      {proposal ? <ProposalDocument q={q} amountInWords={amountInWords} /> : (
      <article className="doc-sheet mx-auto my-6 max-w-[210mm] bg-white p-[14mm] text-[12px] leading-relaxed text-ink-900 shadow-card">
        <header className="flex items-start justify-between gap-6 border-b-2 border-ink-900 pb-4">
          <div>
            <h1 className="text-xl font-bold tracking-tight">{co.displayName ?? co.name}</h1>
            {co.name && co.name !== co.displayName && <p className="text-ink-700">{co.name}</p>}
            {co.address && <p className="mt-1 max-w-xs whitespace-pre-line text-ink-700">{co.address}</p>}
            <p className="mt-1 text-ink-700">{[co.gstin && `GSTIN: ${co.gstin}`, co.email, co.website].filter(Boolean).join('  ·  ')}</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold uppercase tracking-wide">{title}</p>
            <p className="mt-1 font-semibold">{q.quoteNo}</p>
            <p className="text-ink-700">Date: {dateLabel(q.createdAt)}</p>
            {q.validUntil && <p className="text-ink-700">Valid until: {dateLabel(q.validUntil)}</p>}
          </div>
        </header>

        <section className="mt-4 grid grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Billed to</p>
            <p className="font-semibold">{c.company}</p>
            {c.contactPerson && <p>Attn: {c.contactPerson}{c.mobile ? ` · ${c.mobile}` : ''}</p>}
            {c.billingAddress && <p className="whitespace-pre-line text-ink-700">{[c.billingAddress, c.city, c.state, c.pincode].filter(Boolean).join(', ')}</p>}
            {c.gstin && <p>GSTIN: {c.gstin}</p>}
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Project</p>
            <p className="font-semibold">{q.project?.name}</p>
            <p className="text-ink-700">Ref: {q.project?.code}</p>
            {q.subject && <p className="text-ink-700">{q.subject}</p>}
          </div>
        </section>

        <table className="mt-5 w-full border-collapse text-[11px]">
          <thead>
            <tr className="bg-ink-900 text-left text-white">
              <th className="px-2 py-1.5">#</th><th className="px-2 py-1.5">Description</th>
              <th className="px-2 py-1.5 text-right">Qty</th><th className="px-2 py-1.5 text-right">Rate</th>
              {hasDiscount && <th className="px-2 py-1.5 text-right">Discount</th>}
              <th className="px-2 py-1.5 text-right">Taxable</th><th className="px-2 py-1.5 text-right">GST %</th><th className="px-2 py-1.5 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(q.lines as any[]).map((l) => (
              <tr key={l.lineNo} className="border-b border-ink-200 align-top" style={{ breakInside: 'avoid' }}>
                <td className="px-2 py-1.5">{l.lineNo}</td>
                <td className="px-2 py-1.5">
                  <p className="font-semibold">{l.description}</p>
                  {(l.brandName || l.modelNumber) && <p className="text-ink-700">{[l.brandName, l.modelNumber && `Model: ${l.modelNumber}`].filter(Boolean).join(' · ')}</p>}
                  {l.specifications && <p className="text-ink-600">{l.specifications}</p>}
                </td>
                <td className="tabular whitespace-nowrap px-2 py-1.5 text-right">{qtyLabel(l.quantity)} {l.unit}</td>
                <td className="tabular px-2 py-1.5 text-right">{fmt(l.unitPrice)}</td>
                {hasDiscount && <td className="tabular px-2 py-1.5 text-right">{fmt(l.discount)}</td>}
                <td className="tabular px-2 py-1.5 text-right">{fmt(l.taxable)}</td>
                <td className="tabular px-2 py-1.5 text-right">{l.gstRate}%</td>
                <td className="tabular px-2 py-1.5 text-right font-semibold">{fmt(l.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <section className="mt-4 flex items-start justify-between gap-6" style={{ breakInside: 'avoid' }}>
          <div className="max-w-[60%]">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Amount in words</p>
            <p className="font-semibold">{amountInWords(q.grandTotal)}</p>
          </div>
          <dl className="w-64 space-y-1">
            <div className="flex justify-between"><dt>Goods subtotal</dt><dd className="tabular">{fmt(q.goodsSubtotal)}</dd></div>
            {Number(q.discountAmount) > 0 && <div className="flex justify-between"><dt>Discount</dt><dd className="tabular">- {fmt(q.discountAmount)}</dd></div>}
            <div className="flex justify-between"><dt>Taxable value</dt><dd className="tabular">{fmt(q.taxableTotal)}</dd></div>
            <div className="flex justify-between"><dt>GST</dt><dd className="tabular">{fmt(q.gstTotal)}</dd></div>
            <div className="flex justify-between border-t-2 border-ink-900 pt-1 text-sm font-bold"><dt>Grand total (INR)</dt><dd className="tabular">{fmt(q.grandTotal)}</dd></div>
          </dl>
        </section>

        <section className="mt-6 grid grid-cols-2 gap-6" style={{ breakInside: 'avoid' }}>
          {q.paymentTerms && <div><p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Payment terms</p><p>{q.paymentTerms}</p></div>}
          {q.deliveryTerms && <div><p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Delivery</p><p>{q.deliveryTerms}</p></div>}
        </section>
        {q.terms && <section className="mt-4" style={{ breakInside: 'avoid' }}><p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Terms and conditions</p><p className="whitespace-pre-line">{q.terms}</p></section>}
        {q.notes && <section className="mt-4"><p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Notes</p><p className="whitespace-pre-line">{q.notes}</p></section>}

        <footer className="mt-10 flex items-end justify-between gap-6" style={{ breakInside: 'avoid' }}>
          <div className="text-ink-700">
            {q.salesperson && <><p className="text-[10px] font-bold uppercase tracking-wider text-ink-500">Your contact</p>
              <p className="font-semibold text-ink-900">{q.salesperson.name}</p><p>{[q.salesperson.phone, q.salesperson.email].filter(Boolean).join(' · ')}</p></>}
          </div>
          <div className="w-56 text-center">
            <div className="h-14 border-b border-ink-900" />
            <p className="mt-1 font-semibold">Authorised signatory</p>
            <p className="text-ink-700">for {co.displayName ?? co.name}</p>
          </div>
        </footer>
        <p className="mt-6 text-center text-[10px] text-ink-500">This is a computer-generated document.</p>
      </article>)}
    </div>
  );
}
